import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Brain, X, Trash2, Edit3, Check, Loader2, Heart, Info, Calendar, Users, Target, Activity, Star, Fingerprint, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  preference: Heart,
  fact: Info,
  life_event: Calendar,
  relationship: Users,
  goal: Target,
  emotion_pattern: Activity,
  interest: Star,
  identity: Fingerprint,
};

const CATEGORY_COLORS = {
  preference: "text-red-500 bg-red-500/10",
  fact: "text-blue-500 bg-blue-500/10",
  life_event: "text-violet-500 bg-violet-500/10",
  relationship: "text-teal-500 bg-teal-500/10",
  goal: "text-amber-500 bg-amber-500/10",
  emotion_pattern: "text-pink-500 bg-pink-500/10",
  interest: "text-green-500 bg-green-500/10",
  identity: "text-purple-500 bg-purple-500/10",
};

export function MemoryPanel({ open, onClose }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [clearing, setClearing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open) fetchMemories();
  }, [open]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/memories`, { headers });
      setData(resp);
    } catch (err) {
      console.error("Failed to fetch memories", err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (mem) => {
    setEditingId(mem.id);
    setEditText(mem.content);
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/memories/${id}`, { content: editText }, { headers });
      setData(prev => ({
        ...prev,
        memories: prev.memories.map(m => m.id === id ? { ...m, content: editText } : m),
      }));
      setEditingId(null);
      toast.success("Memory updated");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const deleteMemory = async (id) => {
    try {
      await axios.delete(`${API}/memories/${id}`, { headers });
      setData(prev => ({
        ...prev,
        memories: prev.memories.filter(m => m.id !== id),
        total: prev.total - 1,
      }));
      toast.success("Memory removed");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/memories/clear`, { headers });
      setData({ memories: [], grouped: {}, total: 0, categories: data?.categories || {} });
      toast.success("All memories cleared");
    } catch (err) {
      toast.error("Failed to clear");
    } finally {
      setClearing(false);
    }
  };

  if (!open) return null;

  const grouped = {};
  if (data?.memories) {
    data.memories.forEach(m => {
      const cat = m.category || "fact";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(m);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/50 rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-border/30">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold">Omni's Memory</h3>
              <p className="text-xs text-muted-foreground">
                {data?.total || 0} things remembered about you
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="memory-refresh"
              onClick={fetchMemories}
              className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="memory-panel-close"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-140px)] p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.total ? (
            <div className="text-center py-16">
              <Brain className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No memories yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1.5 max-w-[240px] mx-auto">
                As you chat with Omni, it will remember important facts about you — your preferences, life events, goals, and more.
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([cat, mems]) => {
              const Icon = CATEGORY_ICONS[cat] || Info;
              const colorClass = CATEGORY_COLORS[cat] || "text-muted-foreground bg-muted";
              const label = data?.categories?.[cat]?.label || cat;
              return (
                <div key={cat} data-testid={`memory-category-${cat}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={cn("w-6 h-6 rounded-lg flex items-center justify-center", colorClass)}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
                    <span className="text-[10px] text-muted-foreground/50">{mems.length}</span>
                  </div>
                  <div className="space-y-1.5 ml-8">
                    {mems.map(mem => (
                      <div
                        key={mem.id}
                        data-testid={`memory-item-${mem.id}`}
                        className="group flex items-start gap-2 rounded-xl p-2.5 hover:bg-muted/30 transition-colors"
                      >
                        {editingId === mem.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              className="flex-1 text-xs bg-background border border-border rounded-lg px-2.5 py-1.5"
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              onKeyDown={e => e.key === "Enter" && saveEdit(mem.id)}
                              autoFocus
                            />
                            <button onClick={() => saveEdit(mem.id)} className="p-1 text-primary hover:bg-primary/10 rounded-full">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setEditingId(null)} className="p-1 text-muted-foreground hover:bg-muted rounded-full">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="flex-1 text-xs text-foreground/80 leading-relaxed">{mem.content}</p>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              <button
                                data-testid={`edit-memory-${mem.id}`}
                                onClick={() => startEdit(mem)}
                                className="p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-colors"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                              <button
                                data-testid={`delete-memory-${mem.id}`}
                                onClick={() => deleteMemory(mem.id)}
                                className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {data?.total > 0 && (
          <div className="p-4 pt-2 border-t border-border/30 flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground/50">Memories improve Omni's responses. You have full control.</p>
            <button
              data-testid="clear-all-memories"
              onClick={clearAll}
              disabled={clearing}
              className="text-xs text-destructive/70 hover:text-destructive hover:bg-destructive/10 px-3 py-1.5 rounded-full transition-colors"
            >
              {clearing ? "Clearing..." : "Clear All"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
